import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import {
  BG_1,
  BORDER_2,
  FG_1,
  FG_3,
  FONT_BODY,
  RADIUS_LG,
  SHADOW_XS,
} from 'components/upb_viewer_beta/css/betaTokens';

/**
 * BetaPdpSection — collapsible section card used by the beta Product Details
 * tab. Matches the mockup's `.section / .section-hd / .section-bd` block:
 *
 *   ┌──────────────────────────────────────────────────┐
 *   │  Manufacturer & brand                       ⌄    │   ← .section-hd
 *   ├──────────────────────────────────────────────────┤
 *   │  <children>                                       │   ← .section-bd
 *   └──────────────────────────────────────────────────┘
 *
 * Open by default. Header click toggles open/closed; chevron rotates 90°.
 */
function BetaPdpSection({ title, defaultOpen = true, headerRight = null, children }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className={css(styles.section)}>
      <button
        type="button"
        className={css(styles.header)}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <h3 className={css(styles.title)}>{title}</h3>
        <div className={css(styles.headerRight)}>
          {headerRight}
          <svg
            className={css(styles.chev, !open && styles.chevClosed)}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </button>
      {open && <div className={css(styles.body)}>{children}</div>}
    </section>
  );
}

BetaPdpSection.propTypes = {
  title: PropTypes.node.isRequired,
  defaultOpen: PropTypes.bool,
  headerRight: PropTypes.node,
  children: PropTypes.node,
};

BetaPdpSection.defaultProps = {
  defaultOpen: true,
  headerRight: null,
  children: null,
};

const styles = StyleSheet.create({
  section: {
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_XS,
    overflow: 'hidden',
  },

  header: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    userSelect: 'none',
    fontFamily: FONT_BODY,
    textAlign: 'left',
    ':focus': {
      outline: 'none',
    },
    ':focus-visible': {
      outline: 'none',
    },
  },

  title: {
    fontFamily: FONT_BODY,
    fontWeight: 700,
    fontSize: 16,
    color: FG_1,
    margin: 0,
  },

  headerRight: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
  },

  chev: {
    width: 18,
    height: 18,
    color: FG_3,
    transition: 'transform 0.15s',
  },

  chevClosed: {
    transform: 'rotate(-90deg)',
  },

  body: {
    padding: '0 20px 18px',
    borderTop: `1px solid ${BORDER_2}`,
  },
});

export default BetaPdpSection;
